import React, { useEffect, useRef, useState } from 'react';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    TextField, 
    Button, 
    Box,
    MenuItem, 
    CircularProgress,
    Autocomplete
} from '@mui/material';
import { useLanguage } from '../../../../../context/LanguageContext';
import { Store } from '../../../../../types/Store';
import useUpdateStore from '../../../../../hooks/Magasins/useUpdateStore';
import { useStoreData } from '../../../../../context/StoreDataContext';
import { useOptimisticStore } from '../../../../../context/OptimisticStoreContext';
import { toast } from 'react-toastify';
import { phoneCodes } from '../../../../../utils/phoneCodes';
import { cleanPhoneNumber, formatPhoneNumber, formatPhoneInput } from '../../../../../utils/phoneFormatter';
import { checkPostalCode } from '../../../../../services/postalService';

interface EditCoordinatesDrawerProps {
    open: boolean;
    onClose: () => void;
    store: Store;
    onSuccess?: () => void;
}

const EditCoordinatesDrawer = ({ open, onClose, store, onSuccess }: EditCoordinatesDrawerProps) => {
    const { t } = useLanguage();
    const { updateStore, isLoading } = useUpdateStore();
    const { wayTypes, countries, loading: loadingStoreData } = useStoreData();
    const { actualStore, setActualStore } = useOptimisticStore();
    const [zipCode, setZipCode] = useState(store.ZIP_CODE || '');
    const [city, setCity] = useState(store.LOCAL_CITY_NAME || '');
    const [citySuggestions, setCitySuggestions] = useState<string[]>([]);
    const [postalLoading, setPostalLoading] = useState(false);
    const zipTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const address1Ref = useRef<HTMLInputElement | null>(null);
    const address2Ref = useRef<HTMLInputElement | null>(null);
    const address3NumRef = useRef<HTMLInputElement | null>(null);
    const address3TypeRef = useRef<HTMLInputElement | null>(null);
    const address3NameRef = useRef<HTMLInputElement | null>(null);
    const address4Ref = useRef<HTMLInputElement | null>(null);
    const shortCityNameRef = useRef<HTMLInputElement | null>(null);
    const localCountryNameRef = useRef<HTMLInputElement | null>(null);
    const dialingCodeRef = useRef<HTMLInputElement | null>(null);
    const phoneNumberRef = useRef<HTMLInputElement | null>(null);
    const emailAddressRef = useRef<HTMLInputElement | null>(null);
    const faxNumberRef = useRef<HTMLInputElement | null>(null);

    const originalData = {
        address1: store.ADDRESS1 || '',
        address2: store.ADDRESS2 || '',
        address3_num: store.ADDRESS3_NUM || '',
        address3_type: store.ADDRESS3_TYPE || '',
        address3_name: store.ADDRESS3_NAME || '',
        address4: store.ADDRESS4 || '',
        zip_code: store.ZIP_CODE || '',
        local_city_name: store.LOCAL_CITY_NAME || '',
        short_city_name: store.SHORT_CITY_NAME || '',
        local_country_name: store.LOCAL_COUNTRY_NAME || 'FRANCE',
        dialing_code: store.DIALING_CODE || '',
        phone_number: store.PHONE_NUMBER || '',
        email_address: store.EMAIL_ADDRESS || '',
        fax_number: store.FAX_NUMBER || '',
    };

    useEffect(() => {
        if (!open) return;

        setZipCode(store.ZIP_CODE || '');
        setCity(store.LOCAL_CITY_NAME || '');
        setCitySuggestions([]);
    }, [open, store]);

    useEffect(() => () => {
        if (zipTimer.current) {
            clearTimeout(zipTimer.current);
        }
    }, []);

    const handleZipChange = (value: string) => {
        setZipCode(value);

        if (zipTimer.current) {
            clearTimeout(zipTimer.current);
        }

        if (!/^\d{5}$/.test(value)) {
            setCitySuggestions([]);
            setPostalLoading(false);
            return;
        }

        zipTimer.current = setTimeout(async () => {
            setPostalLoading(true);
            const cities = await checkPostalCode(value);
            setCitySuggestions(cities);
            setPostalLoading(false);
        }, 400);
    };

    const handlePhoneBlur = (ref: React.RefObject<HTMLInputElement | null>) => {
        const input = ref.current;
        if (input) {
            const formatted = formatPhoneInput(input.value);
            input.value = formatted;
        }
    };

    const getChangedFields = () => {
        const formData = {
            address1: address1Ref.current?.value || '',
            address2: address2Ref.current?.value || '',
            address3_num: address3NumRef.current?.value || '',
            address3_type: address3TypeRef.current?.value || '',
            address3_name: address3NameRef.current?.value || '',
            address4: address4Ref.current?.value || '',
            zip_code: zipCode,
            local_city_name: city,
            short_city_name: shortCityNameRef.current?.value || '',
            local_country_name: localCountryNameRef.current?.value || 'FRANCE',
            dialing_code: dialingCodeRef.current?.value || '',
            phone_number: phoneNumberRef.current?.value || '',
            email_address: emailAddressRef.current?.value || '',
            fax_number: faxNumberRef.current?.value || '',
        };
        
        const changedFields: Partial<typeof formData> = {};
        let dialingCodeChanged = false;
        
        (Object.keys(formData) as Array<keyof typeof formData>).forEach((key) => {
            if (formData[key] !== originalData[key]) {
                if (key === 'phone_number' || key === 'fax_number') {
                    changedFields[key] = cleanPhoneNumber(formData[key]);
                } else {
                    changedFields[key] = formData[key];
                }
                
                if (key === 'dialing_code') {
                    dialingCodeChanged = true;
                }
            }
        });
        
        // Si l'indicatif a changé, on envoie toujours le numéro de téléphone
        if (dialingCodeChanged && !changedFields.phone_number) {
            changedFields.phone_number = cleanPhoneNumber(formData.phone_number);
        }
        
        return changedFields;
    };

    const handleSave = async () => {
        const changedFields = getChangedFields();
        
        if (Object.keys(changedFields).length === 0) {
            toast.info(t('pages.store.noChanges'));
            onClose();
            return;
        }

        const optimisticUpdates: Partial<Store> = {};
        Object.keys(changedFields).forEach(key => {
            const storeKey = key.toUpperCase() as keyof Store;
            optimisticUpdates[storeKey] = changedFields[key as keyof typeof changedFields];
        });
        
        if (actualStore) {
            setActualStore({ ...actualStore, ...optimisticUpdates } as Store);
        }

        const result = await updateStore(store.ENTITY_ID.toString(), changedFields);
        
        if (result) {
            toast.success(t('pages.store.success.updated'));
            onSuccess?.();
            onClose();
        } else {
            if (actualStore) {
                setActualStore(actualStore);
            }
            toast.error(t('pages.store.errors.updateFailed'));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                {t('pages.store.editCoordinates')}
            </DialogTitle>
            <DialogContent sx={{ paddingTop: '20px!important' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 2 }}>
                        <TextField
                            label={t('pages.store.number')}
                            inputRef={address3NumRef}
                            defaultValue={store.ADDRESS3_NUM || ''}
                            inputProps={{ maxLength: 8 }}
                        />
                        <TextField
                            select
                            label={t('pages.store.wayType')}
                            inputRef={address3TypeRef}
                            defaultValue={store.ADDRESS3_TYPE || ''}
                            disabled={loadingStoreData}
                            SelectProps={{
                                MenuProps: {
                                    sx: { maxHeight: '300px' },
                                },
                            }}
                        >
                            {wayTypes?.map((wayType) => (
                                <MenuItem key={wayType.id} value={wayType.id.toString()}>
                                    {wayType.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label={t('pages.store.wayName')}
                            inputRef={address3NameRef}
                            defaultValue={store.ADDRESS3_NAME || ''}
                            inputProps={{ maxLength: 35 }}
                        />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            label={t('pages.store.zipCode')}
                            value={zipCode}
                            onChange={(e) => handleZipChange(e.target.value)}
                            inputProps={{ maxLength: 5 }}
                            InputProps={{ endAdornment: postalLoading ? <CircularProgress size={14} /> : undefined }}
                        />
                        <Autocomplete
                            freeSolo
                            options={citySuggestions}
                            value={city}
                            inputValue={city}
                            onChange={(_, value) => setCity(value || '')}
                            onInputChange={(_, value) => setCity(value)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('pages.store.city')}
                                    inputProps={{ ...params.inputProps, maxLength: 50 }}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            label={t('pages.store.apartment')}
                            inputRef={address1Ref}
                            defaultValue={store.ADDRESS1 || ''}
                            inputProps={{ maxLength: 38 }}
                        />
                        <TextField
                            label={t('pages.store.entrance')}
                            inputRef={address2Ref}
                            defaultValue={store.ADDRESS2 || ''}
                            inputProps={{ maxLength: 38 }}
                        />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        <TextField
                            label={t('pages.store.cityRef')}
                            inputRef={shortCityNameRef}
                            defaultValue={store.SHORT_CITY_NAME || ''}
                            inputProps={{ maxLength: 38 }}
                        />
                        <TextField
                            label={t('pages.store.locality')}
                            inputRef={address4Ref}
                            defaultValue={store.ADDRESS4 || ''}
                            inputProps={{ maxLength: 38 }}
                        />
                    </Box>

                    <TextField
                        select
                        fullWidth
                        label={t('pages.store.country')}
                        inputRef={localCountryNameRef}
                        defaultValue={store.LOCAL_COUNTRY_NAME || 'FRANCE'}
                        disabled={loadingStoreData}
                        SelectProps={{
                            MenuProps: {
                                sx: { maxHeight: '300px' },
                            },
                        }}
                    >
                        {countries?.map((country) => (
                            <MenuItem key={country.id} value={country.name}>
                                {country.name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        fullWidth
                        type="email"
                        label={t('pages.store.email')}
                        inputRef={emailAddressRef}
                        defaultValue={store.EMAIL_ADDRESS || ''}
                        inputProps={{ maxLength: 50 }}
                    />

                    <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2 }}>
                        <TextField
                            label={t('pages.store.landlinePhone')}
                            inputRef={phoneNumberRef}
                            defaultValue={formatPhoneNumber(store.PHONE_NUMBER) || ''}
                            onBlur={() => handlePhoneBlur(phoneNumberRef)}
                            inputProps={{ maxLength: 30 }}
                        />
                        <TextField
                            select
                            label={t('pages.store.dialingCode')}
                            inputRef={dialingCodeRef}
                            defaultValue={store.DIALING_CODE || ''}
                            SelectProps={{
                                MenuProps: {
                                    sx: { maxHeight: '300px' },
                                },
                            }}
                        >
                            {phoneCodes.map((phone) => (
                                <MenuItem key={`${phone.code}-${phone.country}`} value={phone.code}>
                                    {phone.code} - {phone.country}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    {t('common.cancel')}
                </Button>
                <Button 
                    variant="contained" 
                    onClick={handleSave} 
                    disabled={isLoading}
                    startIcon={isLoading ? <CircularProgress size={20} /> : null}
                >
                    {isLoading ? (t('common.saving')) : (t('common.save'))}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditCoordinatesDrawer;
